import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, Paper, Box, Typography, IconButton, Chip, Divider,
  TextField, CircularProgress, Button, Tooltip, Link,
  useTheme, Collapse,
} from '@mui/material';
import {
  Close as CloseIcon, Block as BlockIcon, LockOpen as UnblockIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  OpenInNew as OpenInNewIcon, Shield as ShieldIcon,
  History as HistoryIcon, BugReport as BugIcon,
  Notes as NotesIcon, Label as LabelIcon,
} from '@mui/icons-material';
import { IOCEntry, HuntMatch } from '../../types';
import { blockIOC, unblockIOC, updateIOC } from '../../store/slices/iocsSlice';
import { IOCsService } from '../../services/iocsService';
import { colorUtils } from '../../theme';
import { getRelativeTimeString, formatDate } from '../../utils/dateUtils';
import api from '../../utils/axiosConfig';
import Draggable from 'react-draggable';

// Cast to any: react-draggable 4.x types incorrectly require all DraggableProps.
const AnyDraggable = Draggable as any;

const DraggablePaper = React.forwardRef<HTMLDivElement, React.ComponentProps<typeof Paper>>(
  function DraggablePaperInner(props, _ref) {
    const nodeRef = React.useRef<HTMLDivElement>(null);
    return (
      <AnyDraggable
        handle="#ioc-dialog-drag-handle"
        cancel={'[class*="MuiDialogContent-root"], button, input, textarea, [role="slider"]'}
        nodeRef={nodeRef}
      >
        <Paper ref={nodeRef} {...props} />
      </AnyDraggable>
    );
  }
);

interface SourceAlert {
  id: string;
  title: string;
  severity: string | null;
  ticket_id: string | null;
  detected_at: string | null;
}

const Section: React.FC<{
  title: string; icon: React.ReactNode; children: React.ReactNode;
  badge?: number; defaultOpen?: boolean;
}> = ({ title, icon, children, badge, defaultOpen = true }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box sx={{ mb: 2 }}>
      <Box
        onClick={() => setOpen(v => !v)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, py: 1, cursor: 'pointer',
          '&:hover .sec-icon': { color: theme.palette.ui?.iconActive ?? theme.palette.primary.main },
        }}
      >
        <Box className="sec-icon" sx={{ color: open ? (theme.palette.ui?.iconActive ?? theme.palette.primary.main) : (theme.palette.ui?.iconDefault ?? theme.palette.text.secondary), display: 'flex' }}>
          {icon}
        </Box>
        <Typography variant="caption" sx={{ fontSize: '0.86rem', flex: 1, fontWeight: 700, letterSpacing: '0.06em', color: theme.palette.text.secondary }}>
          {title}
        </Typography>
        {badge !== undefined && badge > 0 && (
          <Chip label={badge} size="small" sx={{ height: 21, fontSize: '0.75rem', fontWeight: 700, backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.15), color: theme.palette.warning.main }} />
        )}
        <Box sx={{ color: theme.palette.text.tertiary }}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Box>
      </Box>
      <Collapse in={open}>
        <Box sx={{ pl: 3.5, pr: 1, pb: 1 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
      <Typography variant="caption" sx={{ fontSize: '0.86rem', color: theme.palette.text.tertiary, minWidth: 140 }}>{label}</Typography>
      <Box sx={{ flex: 1, textAlign: 'right' }}>{value}</Box>
    </Box>
  );
};

interface IOCDetailDrawerProps {
  ioc: IOCEntry | null;
  onClose: () => void;
  onUpdated: (updated: IOCEntry) => void;
}

const IOCDetailDrawer: React.FC<IOCDetailDrawerProps> = ({ ioc, onClose, onUpdated }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [blocking, setBlocking] = useState(false);

  const [huntResults, setHuntResults] = useState<HuntMatch[] | null>(null);
  const [huntLoading, setHuntLoading] = useState(false);
  const [huntTruncated, setHuntTruncated] = useState(false);

  const [sourceAlert, setSourceAlert] = useState<SourceAlert | null>(null);
  const [sourceLoading, setSourceLoading] = useState(false);

  useEffect(() => {
    if (!ioc) return;
    setNotes(ioc.notes || '');
    setTags(ioc.tags || []);
    setTagInput('');
    setHuntResults(null);
    setSourceAlert(null);

    // Fetch hunt results
    setHuntLoading(true);
    IOCsService.getHuntResults(ioc.id)
      .then(r => { setHuntResults(r.matches); setHuntTruncated(r.truncated); })
      .catch(() => setHuntResults([]))
      .finally(() => setHuntLoading(false));

    // Fetch source alert if alert_enrichment
    if (ioc.source === 'alert_enrichment' && ioc.source_ref) {
      setSourceLoading(true);
      api.get(`/alerts/${ioc.source_ref}`)
        .then(r => setSourceAlert({
          id: r.data.id, title: r.data.title,
          severity: r.data.severity, ticket_id: r.data.ticket_id,
          detected_at: r.data.detected_at,
        }))
        .catch(() => setSourceAlert(null))
        .finally(() => setSourceLoading(false));
    }
  }, [ioc?.id]);

  const saveNotes = useCallback(async () => {
    if (!ioc || notes === (ioc.notes ?? '')) return;
    setSavingNotes(true);
    try {
      const updated = await (dispatch(updateIOC({ id: ioc.id, data: { notes: notes || undefined } }) as any)).unwrap();
      onUpdated(updated);
    } finally { setSavingNotes(false); }
  }, [ioc, notes]);

  const saveTags = useCallback(async (newTags: string[]) => {
    if (!ioc) return;
    const updated = await (dispatch(updateIOC({ id: ioc.id, data: { tags: newTags } }) as any)).unwrap();
    onUpdated(updated);
  }, [ioc]);

  const handleTagKeyDown = async (e: React.KeyboardEvent) => {
    if ((e.key !== 'Enter' && e.key !== ',') || !tagInput.trim()) return;
    e.preventDefault();
    const tag = tagInput.trim();
    if (tags.includes(tag)) { setTagInput(''); return; }
    const newTags = [...tags, tag];
    setTags(newTags);
    setTagInput('');
    await saveTags(newTags);
  };

  const handleRemoveTag = async (tag: string) => {
    const newTags = tags.filter(t => t !== tag);
    setTags(newTags);
    await saveTags(newTags);
  };

  const handleBlock = async () => {
    if (!ioc) return;
    setBlocking(true);
    try {
      const updated = await (dispatch as any)(
        ioc.is_currently_blocked ? unblockIOC(ioc.id) : blockIOC({ id: ioc.id })
      ).unwrap();
      onUpdated(updated);
    } finally { setBlocking(false); }
  };

  const sevColor = (sev: string | null) => {
    if (!sev) return theme.palette.text.disabled;
    const s = sev.toLowerCase();
    if (s === 'critical') return theme.palette.severity?.critical ?? theme.palette.error.main;
    if (s === 'high') return theme.palette.severity?.high ?? theme.palette.warning.main;
    if (s === 'medium') return theme.palette.severity?.medium ?? theme.palette.info.main;
    return theme.palette.text.secondary;
  };

  if (!ioc) return null;

  const vd = ioc.vendor_data || {};
  const hasAbuseIPDB = vd.country_code || vd.isp || vd.total_reports != null;
  const hasVT = vd.detection_count != null;

  return (
    <Dialog
      open={!!ioc}
      onClose={onClose}
      hideBackdrop
      disableEnforceFocus
      disableScrollLock
      PaperComponent={DraggablePaper}
      PaperProps={{
        sx: {
          width: '62vw',
          maxWidth: '900px',
          minWidth: '520px',
          height: '93vh',
          maxHeight: '93vh',
          m: 0,
          position: 'fixed',
          top: '2vh',
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.border?.light ?? theme.palette.divider}`,
          borderRadius: '12px',
          boxShadow: '0 0 0 1px oklch(100% 0 0 / 6%), 0 8px 32px -4px oklch(0% 0 0 / 40%), 0 32px 80px -8px oklch(0% 0 0 / 60%)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          pointerEvents: 'auto',
        },
      }}
      sx={{ pointerEvents: 'none', '& .MuiDialog-container': { pointerEvents: 'none' } }}
    >
      {/* Drag grip */}
      <Box
        id="ioc-dialog-drag-handle"
        sx={{
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          height: 18, flexShrink: 0, cursor: 'move',
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.border?.light ?? theme.palette.divider}`,
        }}
      >
        <Box sx={{ width: 32, height: 3, borderRadius: 2, backgroundColor: theme.palette.text.disabled, opacity: 0.5 }} />
      </Box>
      {/* Header */}
      <Box sx={{
        px: 4, py: 3,
        borderBottom: `1px solid ${theme.palette.border?.light ?? theme.palette.divider}`,
        display: 'flex', alignItems: 'flex-start', gap: 1,
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Chip label={ioc.ioc_type} size="small" sx={{ height: 21, fontSize: '0.75rem', fontWeight: 700 }} />
            <Chip
              label={ioc.source.replace('_', ' ')}
              size="small"
              sx={{
                height: 21, fontSize: '0.71rem', fontWeight: 700,
                backgroundColor: colorUtils.withOpacity(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
              }}
            />
            {ioc.is_currently_blocked && (
              <Chip label="BLOCKED" size="small" sx={{ height: 21, fontSize: '0.71rem', fontWeight: 700, backgroundColor: colorUtils.withOpacity(theme.palette.error.main, 0.12), color: theme.palette.error.main }} />
            )}
          </Box>
          <Typography
            variant="body2"
            sx={{ fontFamily: 'monospace', fontSize: '1.03rem', fontWeight: 600, wordBreak: 'break-all' }}
          >
            {ioc.ioc_value}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 21 }} /></IconButton>
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 4, pt: 2.5, pb: 4 }}>

        {/* Enrichment */}
        <Section title="ENRICHMENT" icon={<BugIcon fontSize="small" />}>
          {(hasAbuseIPDB || hasVT) ? (
            <>
              {hasAbuseIPDB && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.86rem', fontWeight: 700, color: theme.palette.info.main, display: 'block', mb: 0.75 }}>AbuseIPDB</Typography>
                  <Row label="Confidence" value={<Typography variant="caption" sx={{ fontSize: '0.86rem', fontWeight: 700, color: (vd.abuse_confidence ?? ioc.reputation_score ?? 0) > 25 ? theme.palette.error.main : theme.palette.success.main }}>{vd.abuse_confidence ?? ioc.reputation_score ?? '—'}%</Typography>} />
                  <Row label="Total reports" value={<Typography variant="caption" sx={{ fontSize: '0.86rem' }}>{vd.total_reports ?? '—'}</Typography>} />
                  <Row label="ISP" value={<Typography variant="caption" sx={{ fontSize: '0.86rem', wordBreak: 'break-all' }}>{vd.isp ?? ioc.isp ?? '—'}</Typography>} />
                  <Row label="Country" value={<Typography variant="caption" sx={{ fontSize: '0.86rem' }}>{vd.country_code ?? ioc.country_code ?? '—'}</Typography>} />
                  <Row label="Usage" value={<Typography variant="caption" sx={{ fontSize: '0.86rem' }}>{vd.usage_type ?? ioc.usage_type ?? '—'}</Typography>} />
                  <Row label="TOR" value={<Typography variant="caption" sx={{ fontSize: '0.86rem' }}>{(vd.is_tor ?? ioc.is_tor) ? 'Yes' : 'No'}</Typography>} />
                  {vd.last_reported && <Row label="Last reported" value={<Typography variant="caption" sx={{ fontSize: '0.86rem' }}>{formatDate(vd.last_reported)}</Typography>} />}
                </Box>
              )}
              {hasVT && (
                <Box>
                  <Typography variant="caption" sx={{ fontSize: '0.86rem', fontWeight: 700, color: theme.palette.warning.main, display: 'block', mb: 0.75 }}>VirusTotal</Typography>
                  <Row label="Detections" value={<Typography variant="caption" sx={{ fontSize: '0.86rem', fontWeight: 700, color: (vd.detection_count ?? 0) > 0 ? theme.palette.error.main : theme.palette.success.main }}>{vd.detection_count ?? 0} / {vd.total_engines ?? '?'}</Typography>} />
                  {vd.permalink && <Row label="Report" value={<Link href={vd.permalink} target="_blank" rel="noopener" sx={{ fontSize: '0.86rem', display: 'flex', alignItems: 'center', gap: 0.25, justifyContent: 'flex-end' }}>View on VT <OpenInNewIcon sx={{ fontSize: 14 }} /></Link>} />}
                  {(vd.detection_names?.length > 0) && (
                    <Box sx={{ mt: 0.75 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.86rem', color: theme.palette.text.tertiary, display: 'block', mb: 0.5 }}>Detection names</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(vd.detection_names as string[]).slice(0, 12).map(n => (
                          <Chip key={n} label={n} size="small" sx={{ height: 21, fontSize: '0.69rem', backgroundColor: colorUtils.withOpacity(theme.palette.error.main, 0.08), color: theme.palette.error.main }} />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
              {ioc.enrichment_expires_at && (
                <Typography variant="caption" sx={{ fontSize: '0.86rem', display: 'block', mt: 1.5, color: theme.palette.text.tertiary }}>
                  Cache expires {getRelativeTimeString(ioc.enrichment_expires_at)}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="caption" sx={{ fontSize: '0.86rem', color: theme.palette.text.disabled }}>
              {ioc.last_enriched_at ? 'No threat data returned' : 'Enrichment pending…'}
            </Typography>
          )}
        </Section>

        <Divider sx={{ my: 1.5 }} />

        {/* Source alert */}
        {ioc.source === 'alert_enrichment' && (
          <>
            <Section title="SOURCE ALERT" icon={<ShieldIcon fontSize="small" />}>
              {sourceLoading ? (
                <CircularProgress size={16} />
              ) : sourceAlert ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Typography variant="body2" sx={{ fontSize: '1rem', fontWeight: 500 }}>{sourceAlert.title}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {sourceAlert.severity && (
                      <Chip label={sourceAlert.severity} size="small" sx={{ height: 21, fontSize: '0.71rem', fontWeight: 700, backgroundColor: colorUtils.withOpacity(sevColor(sourceAlert.severity), 0.12), color: sevColor(sourceAlert.severity) }} />
                    )}
                    {sourceAlert.detected_at && (
                      <Typography variant="caption" sx={{ fontSize: '0.86rem', color: theme.palette.text.tertiary }}>{formatDate(sourceAlert.detected_at)}</Typography>
                    )}
                  </Box>
                  {sourceAlert.ticket_id && (
                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
                      onClick={() => { onClose(); navigate('/tickets'); }}
                      sx={{ textTransform: 'none', fontSize: '0.86rem', alignSelf: 'flex-start', mt: 0.5 }}
                    >
                      View ticket
                    </Button>
                  )}
                </Box>
              ) : (
                <Typography variant="caption" sx={{ fontSize: '0.86rem', color: theme.palette.text.disabled }}>Alert not available</Typography>
              )}
            </Section>
            <Divider sx={{ my: 1.5 }} />
          </>
        )}

        {/* Blocklist */}
        <Section title="BLOCKLIST" icon={<BlockIcon fontSize="small" />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Button
              size="small"
              variant={ioc.is_currently_blocked ? 'outlined' : 'contained'}
              color={ioc.is_currently_blocked ? 'inherit' : 'error'}
              startIcon={blocking ? <CircularProgress size={14} /> : ioc.is_currently_blocked ? <UnblockIcon /> : <BlockIcon />}
              disabled={blocking}
              onClick={handleBlock}
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.9rem' }}
            >
              {ioc.is_currently_blocked ? 'Unblock' : 'Block'}
            </Button>
            {ioc.is_blocked && (
              <Typography variant="caption" sx={{ fontSize: '0.86rem', color: theme.palette.text.secondary }}>
                {ioc.blocked_until ? `Expires ${getRelativeTimeString(ioc.blocked_until)}` : 'Permanent'}
              </Typography>
            )}
          </Box>
          {!ioc.is_blocked && (
            <Typography variant="caption" sx={{ fontSize: '0.86rem', color: theme.palette.text.tertiary }}>
              Decay: IP → 14d · domain/url → 30d · file hash → permanent
            </Typography>
          )}
        </Section>

        <Divider sx={{ my: 1.5 }} />

        {/* Notes */}
        <Section title="ANALYST NOTES" icon={<NotesIcon fontSize="small" />}>
          <TextField
            fullWidth multiline rows={3} size="small"
            placeholder="Add investigation notes…"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            onBlur={saveNotes}
            InputProps={{
              endAdornment: savingNotes ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.94rem',
                backgroundColor: theme.palette.background.default,
                '& fieldset': { borderColor: theme.palette.border?.light ?? theme.palette.divider },
              },
            }}
          />
        </Section>

        <Divider sx={{ my: 1.5 }} />

        {/* Tags */}
        <Section title="TAGS" icon={<LabelIcon fontSize="small" />}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: tags.length ? 1 : 0 }}>
            {tags.map(tag => (
              <Chip
                key={tag} label={tag} size="small"
                onDelete={() => handleRemoveTag(tag)}
                sx={{ height: 23, fontSize: '0.78rem', fontWeight: 600 }}
              />
            ))}
          </Box>
          <TextField
            fullWidth size="small"
            placeholder="Add tag and press Enter…"
            value={tagInput}
            onChange={e => setTagInput(e.target.value)}
            onKeyDown={handleTagKeyDown}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.94rem',
                backgroundColor: theme.palette.background.default,
                '& fieldset': { borderColor: theme.palette.border?.light ?? theme.palette.divider },
              },
            }}
          />
        </Section>

        <Divider sx={{ my: 1.5 }} />

        {/* Historical Exposure */}
        <Section
          title="HISTORICAL EXPOSURE"
          icon={<HistoryIcon fontSize="small" />}
          badge={huntResults?.length}
          defaultOpen={true}
        >
          {huntLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" sx={{ fontSize: '0.86rem', color: theme.palette.text.tertiary }}>Scanning historical alerts…</Typography>
            </Box>
          ) : huntResults === null ? null : huntResults.length === 0 ? (
            <Typography variant="caption" sx={{ fontSize: '0.86rem', color: theme.palette.text.disabled }}>
              No historical matches found — IOC was not seen in prior alerts.
            </Typography>
          ) : (
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.86rem', color: theme.palette.text.tertiary, display: 'block', mb: 1 }}>
                This indicator appeared in {huntResults.length}{huntTruncated ? '+' : ''} alert{huntResults.length !== 1 ? 's' : ''} before it was flagged.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {huntResults.map(match => (
                  <Box
                    key={match.alert_id}
                    sx={{
                      p: 1, borderRadius: 1,
                      backgroundColor: theme.palette.background.default,
                      border: `1px solid ${theme.palette.border?.light ?? theme.palette.divider}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                      {match.severity && (
                        <Chip label={match.severity} size="small" sx={{ height: 18, fontSize: '0.67rem', fontWeight: 700, backgroundColor: colorUtils.withOpacity(sevColor(match.severity), 0.12), color: sevColor(match.severity) }} />
                      )}
                      <Typography variant="caption" sx={{ fontSize: '0.86rem', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {match.alert_title}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.8rem' }}>
                        {match.detected_at ? getRelativeTimeString(match.detected_at) : '—'}
                      </Typography>
                      {match.ticket_id && (
                        <Tooltip title="Open ticket">
                          <IconButton size="small" onClick={() => { onClose(); navigate('/tickets'); }} sx={{ p: 0.25 }}>
                            <OpenInNewIcon sx={{ fontSize: 15, color: theme.palette.primary.main }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                ))}
                {huntTruncated && (
                  <Typography variant="caption" sx={{ fontSize: '0.86rem', color: theme.palette.text.tertiary, textAlign: 'center' }}>
                    Showing first 50 matches
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Section>
      </Box>
    </Dialog>
  );
};

export default IOCDetailDrawer;
